"use client";

import { useEffect, useState, useCallback } from "react";
import posthog from "posthog-js";
import {
  ShieldAlert,
  RefreshCw,
  Home,
  Terminal,
  CircuitBoard,
  Copy,
  Check,
  Eye,
  EyeOff,
} from "lucide-react";
import { isNextNavigationError } from "@/lib/auth/next-navigation-errors";

/**
 * Global (root) error boundary.
 *
 * IMPORTANT: `redirect()` and `notFound()` from `next/navigation` throw
 * sentinel errors (digests `NEXT_REDIRECT` / `NEXT_NOT_FOUND`) that Next's
 * framework is expected to catch. These can bubble up here during the
 * logout → back-button flow when the server-rendered authenticated layout
 * runs `redirect("/login")` mid-transition. If we render the critical-error
 * UI, the user sees an "UNCAUGHT_EXCEPTION: NEXT_REDIRECT" overlay instead
 * of the intended navigation.
 *
 * We re-throw those sentinels so Next can process them correctly.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [showScanline, setShowScanline] = useState(true);
  const [devMode, setDevMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const isSentinel = isNextNavigationError(error);

  useEffect(() => {
    // Re-throw framework sentinels so Next can handle the navigation
    // (e.g. the logout-flow `redirect("/login")`) instead of us
    // surfacing it as a critical error.
    if (isSentinel) {
      throw error;
    }

    // Capture real exceptions to PostHog
    if (typeof window !== "undefined" && posthog) {
      posthog.captureException(error, {
        $exception_type: error.name,
        $exception_message: error.message,
        $exception_stack: error.stack,
        error_id: error.digest,
        error_tier: "critical",
        context: "global_error_boundary",
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }

    console.error("[global_error_boundary] Critical error:", error);

    const timer = setTimeout(() => setShowScanline(false), 200);
    return () => clearTimeout(timer);
  }, [error, isSentinel]);

  const handleRetry = useCallback(() => {
    const newCount = retryCount + 1;
    setRetryCount(newCount);

    if (typeof window !== "undefined" && posthog) {
      posthog.capture("error_retry_clicked", {
        error_id: error.digest,
        error_tier: "critical",
        retry_count: newCount,
        context: "global_error_boundary",
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }

    setShowScanline(true);
    setTimeout(() => {
      setShowScanline(false);
      reset();
    }, 150);
  }, [error, retryCount, reset]);

  const handleGoHome = useCallback(() => {
    if (typeof window !== "undefined" && posthog) {
      posthog.capture("error_go_home_clicked", {
        error_id: error.digest,
        error_tier: "critical",
        context: "global_error_boundary",
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }
    window.location.href = "/";
  }, [error]);

  const handleCopyErrorId = useCallback(() => {
    if (error.digest) {
      navigator.clipboard.writeText(error.digest);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [error.digest]);

  if (isSentinel) {
    // Render nothing while the effect re-throws; avoids flash of the
    // critical-error UI during the framework's redirect processing.
    return null;
  }

  return (
    <html lang="en">
      <head>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              @keyframes error-scanline-pass {
                0% { transform: translateY(-100%); opacity: 0.8; }
                100% { transform: translateY(100vh); opacity: 0; }
              }
              @keyframes error-heartbeat-glow {
                0%, 100% { box-shadow: 0 0 0 0 rgba(244, 244, 245, 0); }
                50% { box-shadow: 0 0 20px 4px rgba(244, 244, 245, 0.15); }
              }
              @keyframes error-circuit-pulse {
                0%, 100% { opacity: 0.02; }
                50% { opacity: 0.04; }
              }
              @keyframes error-content-entry {
                0% { opacity: 0; transform: translateY(8px); filter: blur(4px); }
                100% { opacity: 1; transform: translateY(0); filter: blur(0); }
              }
              @keyframes error-ribbon-slide {
                0% { transform: translateX(-100%); opacity: 0; }
                100% { transform: translateX(0); opacity: 1; }
              }
              .global-error-scanline { animation: error-scanline-pass 150ms ease-out forwards; }
              .global-error-content { animation: error-content-entry 300ms ease-out both; }
              .global-error-btn-primary { animation: error-heartbeat-glow 3s ease-in-out infinite; }
              .global-error-ribbon { animation: error-ribbon-slide 300ms ease-out 400ms both; }
              .global-error-circuit { animation: error-circuit-pulse 4s ease-in-out infinite; }
            `,
          }}
        />
      </head>
      <body className="m-0 min-h-screen bg-zinc-950 font-sans text-zinc-100">
        {/* Scanline overlay */}
        {showScanline && (
          <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
            <div className="global-error-scanline absolute inset-x-0 h-0.5 bg-gradient-to-b from-transparent via-rose-500/20 to-transparent" />
          </div>
        )}

        {/* Circuit background pattern */}
        <div
          className="global-error-circuit pointer-events-none fixed inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          aria-hidden="true"
        />

        {/* Main content */}
        <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-8 pb-20">
          <div className="global-error-content w-full max-w-md text-center">
            {/* Icon */}
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-zinc-900 ring-1 ring-rose-500/30">
              <ShieldAlert className="h-8 w-8 text-zinc-100" />
            </div>

            {/* Title */}
            <h1 className="mb-3 text-2xl font-bold tracking-tight text-zinc-100">
              Critical System Error
            </h1>

            {/* Description */}
            <p className="mb-8 text-sm leading-relaxed text-zinc-400">
              A critical error has occurred and the application could not
              recover. Please try again or return to the home page.
            </p>

            {/* Action buttons */}
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={handleRetry}
                className="global-error-btn-primary inline-flex items-center justify-center gap-2 rounded-md bg-zinc-100 px-5 py-2.5 text-sm font-medium text-zinc-950 transition-all duration-200 hover:bg-white hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-100 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>

              <button
                onClick={handleGoHome}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2.5 text-sm font-medium text-zinc-300 transition-all duration-200 hover:border-zinc-700 hover:bg-zinc-800 hover:text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <Home className="h-4 w-4" />
                Go to Home
              </button>
            </div>

            {/* Debug HUD (dev mode) */}
            {devMode && error && (
              <div className="mt-6 w-full rounded-lg border border-rose-500/30 bg-zinc-950 p-4 text-left">
                <div className="mb-3 flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-rose-500" />
                  <span className="font-mono text-xs font-medium tracking-wide text-rose-500">
                    DEBUG OUTPUT
                  </span>
                </div>

                <div className="mb-2">
                  <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
                    Exception
                  </span>
                  <p className="font-mono text-sm text-rose-400">
                    {error.name}
                  </p>
                </div>

                <div className="mb-3">
                  <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
                    Message
                  </span>
                  <p className="break-words font-mono text-xs text-zinc-300">
                    {error.message}
                  </p>
                </div>

                {error.stack && (
                  <div>
                    <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
                      Stack Trace
                    </span>
                    <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-900 p-3 font-mono text-size-11 leading-relaxed text-zinc-400">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Dev mode toggle */}
        {process.env.NODE_ENV === "development" && (
          <button
            onClick={() => setDevMode(!devMode)}
            className="fixed bottom-14 right-4 z-50 inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-size-10 text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-400"
          >
            {devMode ? (
              <Eye className="h-3 w-3" />
            ) : (
              <EyeOff className="h-3 w-3" />
            )}
            <span>{devMode ? "DEV VIEW" : "USER VIEW"}</span>
          </button>
        )}

        {/* Error ID ribbon */}
        {error.digest && (
          <div className="global-error-ribbon fixed inset-x-0 bottom-0 z-40 border-t border-zinc-800 bg-zinc-900/95 px-4 py-2.5 backdrop-blur-sm">
            <div className="mx-auto flex max-w-2xl items-center justify-between">
              <div className="flex items-center gap-3">
                <CircuitBoard className="h-4 w-4 text-zinc-600" />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Error ID:</span>
                  <code className="font-mono text-size-11 tracking-wide text-zinc-400">
                    {error.digest}
                  </code>
                </div>
              </div>
              <button
                onClick={handleCopyErrorId}
                className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3" />
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </body>
    </html>
  );
}
